import { getChartDevice } from '@/api/device-chart'
import { createRequestCancellation, readStoredToken } from '@/api/http'
import { responseBody } from './device-chart-data'
import { readonlyDeviceStream, sharedReadOptions, isStaticDataPreview } from './shared-view'

// 当前画布按实际设备共享；编辑示例与运行页面设备都复用此入口。
// 不能跨编辑器/预览、账号、分享授权代次或页面复用。
const scopes = new WeakMap()
let serial = 0
const noop = () => {}
const clone = value => JSON.parse(JSON.stringify(value))

export function usesPageDeviceProperties (parameter, host, resolvedDeviceId) {
  // 编辑态使用组件实际解析出的示例/固定设备，不把页面导航参数误当成示例。
  // 运行态仍只对页面传入的设备合并，缺参时不能回退旧示例设备。
  const editing = !!host.menuFlag
  const id = editing ? resolvedDeviceId === undefined ? parameter && parameter.deviceId : resolvedDeviceId
    : host.navigationContext && host.navigationContext.deviceId
  return parameter && (editing || parameter.deviceSource === 'page') && typeof id === 'string' && /^[A-Za-z0-9_-]{1,256}$/.test(id) && !isStaticDataPreview(host) &&
    !host.isTemplateEditing && !host.isComponentTemplateEditing && !host.isTemplatePreview && !(host.obj && host.obj.templatePreview)
}

function identity (host) {
  let token = ''
  if (!host.isSharedView && typeof readStoredToken === 'function' && typeof localStorage !== 'undefined') token = readStoredToken()
  const user = host.$store && host.$store.getters && host.$store.getters['user/userInfo'] || {}
  return JSON.stringify([host.activePageId, host.projectRecord && host.projectRecord.id, host.navigationContext && host.navigationContext.deviceId,
    !!host.menuFlag, !!host.isPreview, !!host.liveProjectPreview, !!host.isSharedView, !!host.isSharedDataView,
    !!host.isTemplateEditing, !!host.isComponentTemplateEditing, !!host.isTemplatePreview, !!(host.obj && host.obj.templatePreview),
    host.generation, host.shareUserId, user.id || user.username, token])
}

function release (subscription) {
  if (subscription) { try { subscription.unsubscribe() } catch (_) { /* 原连接可能已经关闭。 */ } }
}

// history=1 对每个属性回送平台最新值；后续仍是 MeasurementValue，不改变原 formatter 输入协议。
export function pageDevicePropertyStream (host, deviceId, property, { replay = true } = {}) {
  let reconnect = noop
  return {
    onReconnect (callback) { reconnect = callback; return this },
    subscribe (next = noop, error = noop, complete = noop) {
      if (!/^[A-Za-z0-9_-]{1,256}$/.test(deviceId || '') || typeof property !== 'string' || !property || property.length > 256) throw new Error('页面设备属性绑定无效')
      const scope = identity(host), readClient = host.sharedReadClient
      const valid = () => !host._isDestroyed && identity(host) === scope && host.sharedReadClient === readClient &&
        (!host.isSharedView || (host.isSharedDataView && host.sharedDataCurrent()))
      if (!valid()) throw new Error('页面设备访问身份已失效，请重新验证')
      if (!scopes.has(host)) scopes.set(host, new Map())
      const entries = scopes.get(host), key = JSON.stringify([scope, deviceId])
      // 旧页面仍在销毁过程中时，也不能留下旧设备订阅或复用它的缓存。
      for (const old of entries.values()) if (!old.valid()) old.close()
      let entry = entries.get(key)
      if (!entry) {
        entry = { members: new Set(), listeners: new Map(), values: new Map(), closed: false, valid, request: createRequestCancellation() }
        entries.set(key, entry)
        entry.remove = member => {
          entry.members.delete(member)
          const listeners = entry.listeners.get(member.property)
          if (listeners) { listeners.delete(member); if (!listeners.size) entry.listeners.delete(member.property) }
        }
        entry.close = () => {
          if (entry.closed) return
          entry.closed = true
          if (entries.get(key) === entry) entries.delete(key)
          entry.request.cancel('页面设备订阅已释放')
          const handle = entry.handle; entry.handle = null
          release(handle); entry.values.clear(); entry.members.clear(); entry.listeners.clear()
        }
        const terminal = (cause, finished = false) => {
          if (entry.closed) return
          const members = Array.from(entry.members); entry.members.clear(); entry.close()
          members.forEach(member => {
            if (member.closed) return
            member.closed = true
            try { if (finished) member.complete(); else member.error(cause) } catch (_) { /* 一个组件不能阻断其他组件的清理。 */ }
          })
        }
        const active = () => {
          if (entry.closed) return false
          if (!entry.valid()) { terminal(new Error('页面设备或访问身份已变化')); return false }
          return true
        }
        const deliver = (member, message) => {
          if (member.closed || !active()) return
          try { member.next(clone(message)) } catch (cause) {
            member.closed = true; entry.remove(member)
            try { member.error(cause) } catch (_) { /* 隔离组件回调异常。 */ }
            if (!entry.members.size) entry.close()
          }
        }
        entry.deliver = deliver
        // Promise 让同一轮挂载的组件先登记，共用一次产品解析和一条订阅。
        entry.ready = Promise.resolve().then(async () => {
          try {
            if (!active()) return
            const options = sharedReadOptions(host, { cancelToken: entry.request.token })
            const body = responseBody(await getChartDevice(deviceId, options))
            if (!active()) return
            const device = body.result, productId = device && device.productId
            if (!device || (device.id !== undefined && device.id !== deviceId) || typeof productId !== 'string' || !/^[A-Za-z0-9_-]{1,256}$/.test(productId)) throw new Error('设备产品信息无效，无法订阅实时属性')
            const stream = readonlyDeviceStream(host, `page-properties-${Date.now()}-${++serial}`,
              `/dashboard/device/${productId}/properties/realTime`, { deviceId, history: 1 })
            if (stream.onReconnect) stream.onReconnect(() => {
              if (!active()) return
              // 重连后的首值重新由平台回送，禁止将旧连接缓存当成新的首值。
              entry.values.clear()
              entry.members.forEach(member => {
                if (!member.closed) Promise.resolve().then(() => { if (!member.closed && active()) return member.reconnect() }).catch(member.error)
              })
            })
            const handle = stream.subscribe(message => {
              if (!active()) return
              try {
                if (message && message.success === false) throw new Error(message.message || '实时属性订阅失败')
                if (!message || !message.payload) return // 订阅确认不是属性值。
                const payload = message.payload, row = payload.value
                if (!row || typeof row !== 'object' || typeof row.property !== 'string' || !row.property || !Object.prototype.hasOwnProperty.call(row, 'value')) throw new Error('平台返回的多属性格式不正确')
                if (row.deviceId && String(row.deviceId) !== deviceId) return
                if (!['number', 'string'].includes(typeof payload.timestamp) || !String(payload.timestamp).trim() || !Number.isFinite(Number(payload.timestamp))) throw new Error('平台返回的属性时间不正确')
                const previous = entry.values.get(row.property)
                if (!previous || Number(previous.payload.timestamp) <= Number(payload.timestamp)) entry.values.set(row.property, clone(message))
                Array.from(entry.listeners.get(row.property) || []).forEach(member => deliver(member, message))
              } catch (cause) { terminal(cause) }
            }, cause => terminal(cause || new Error('实时属性订阅失败')), () => terminal(null, true))
            if (entry.closed) release(handle); else entry.handle = handle
          } catch (cause) { if (!entry.closed) terminal(cause) }
        })
      }
      const member = { property, next, error, complete, reconnect, closed: false }
      entry.members.add(member)
      if (!entry.listeners.has(property)) entry.listeners.set(property, new Set())
      entry.listeners.get(property).add(member)
      const latest = entry.values.get(property)
      if (replay && latest) entry.deliver(member, latest)
      return { ready: entry.ready, get closed () { return member.closed || entry.closed }, unsubscribe () {
        if (member.closed) return
        member.closed = true; entry.remove(member)
        if (!entry.members.size) entry.close()
      } }
    }
  }
}
